import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  formatLakh,
  formatPercent,
  getAchievementToneClass,
} from "../utils/formatters";

export default function AsyncFilterDropdown({
  label,
  placeholder,
  value,
  options,
  loading,
  error,
  disabled,
  onOpen,
  onSelect,
  searchable = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleOutsideClick(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
  }, [isOpen]);

  function handleToggle() {
    if (disabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen && onOpen) {
      onOpen();
    }
    if (!nextOpen) {
      setSearchTerm("");
    }
  }

  function handleSelect(option) {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm("");
  }

  // Normalize selected value for display
  const displayValue = value?.name || value?.label || value?.value || "";

  // Check selected option
  const isSelected = (option) => {
    return (
      value?.id === option.id ||
      value?.value === option.value ||
      value?.label === option.label ||
      value?.name === option.name
    );
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options || [];
    const q = searchTerm.trim().toLowerCase();
    return (options || []).filter((option) => {
      const text = (option.name || option.label || option.value || "").toLowerCase();
      return text.includes(q);
    });
  }, [options, searchTerm, searchable]);

  const optionCount = (options || []).length;

  return (
    <div className="sd-dropdown" ref={wrapperRef}>
      <div className="sd-dropdown__label">
        {label}
        {optionCount > 0 && !disabled && (
          <span className="sd-dropdown__count">{optionCount}</span>
        )}
      </div>

      <div
        className={[
          "sd-dropdown__header",
          disabled ? "sd-dropdown__header--disabled" : "",
          displayValue ? "sd-dropdown__header--active" : "",
        ].join(" ")}
        onClick={handleToggle}
        title={disabled ? `Select required filters first` : ""}
      >
        <span className="sd-dropdown__value">
          {displayValue || placeholder || `Select ${label}`}
        </span>
        {loading ? (
          <span className="sd-spinner" />
        ) : (
          <span className={`sd-dropdown__arrow ${isOpen ? "sd-dropdown__arrow--open" : ""}`}>
            ▾
          </span>
        )}
      </div>

      {isOpen && (
        <div className="sd-dropdown__list">
          {/* Search — show if searchable and more than 6 options */}
          {searchable && optionCount > 6 && (
            <div className="sd-dropdown__search-wrap">
              <input
                ref={searchRef}
                className="sd-dropdown__search"
                type="text"
                placeholder={`Search ${label}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {loading && (
            <div className="sd-dropdown__message">Loading...</div>
          )}

          {!loading && error && (
            <div className="sd-dropdown__message sd-dropdown__error">
              {error}
            </div>
          )}

          {!loading && !error && filteredOptions.length === 0 && (
            <div className="sd-dropdown__message">
              {searchTerm ? "No matches found" : "No options available"}
            </div>
          )}

          {!loading &&
            !error &&
            filteredOptions.map((option) => (
              <button
                type="button"
                key={option.id || option.value || option.label}
                className={`sd-dropdown__item ${
                  isSelected(option) ? "sd-dropdown__item--selected" : ""
                }`}
                onClick={() => handleSelect(option)}
              >
                <div className="sd-dropdown__item-row">
                  <div className="sd-dropdown__item-title">
                    {option.name || option.label || option.value}
                  </div>

                  {(option.salesLakh !== undefined ||
                    option.employees !== undefined ||
                    option.achievement !== undefined) && (
                    <div className="sd-dropdown__item-meta">
                      {option.salesLakh !== undefined && (
                        <span className="sd-dropdown__meta-text">
                          Sales: {formatLakh(option.salesLakh)}
                        </span>
                      )}
                      {option.employees !== undefined && (
                        <span className="sd-dropdown__meta-text">
                          {option.employees} employees
                        </span>
                      )}
                      {option.achievement !== undefined && (
                        <span
                          className={`sd-badge sd-badge--${getAchievementToneClass(option.achievement)}`}
                        >
                          {formatPercent(option.achievement)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
